import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { QuotationData, Currency, TollDetails, FuelUnit } from '../types';
import { convertCurrency, getCurrencySymbol } from './currencyUtils';
import { calculateCostWithTax } from './calculationUtils';
import { fetchImageAsDataUrl } from './imageUtils';
import i18n from '../i18n';
import { getCargoRiskDetails } from './cargoDetailsUtils';
import {
  COMPANY_LOGO,
  WATERMARK_IMAGE
} from './imageAssets';

// Define colors to match the UI design
const COLORS = {
  primary: '#030e2e',
  primaryDark: '#020a22',
  primaryLight: '#061543',
  green: '#16a34a',
  red: '#dc2626',
  gray: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827'
  },
  white: '#ffffff',
  blue: {
    50: '#eff6ff',
    600: '#2563eb',
    700: '#1d4ed8'
  },
  amber: {
    50: '#fffbeb',
    600: '#d97706'
  }
};

// Helper function to get translation
const t = (key: string, options?: any) => {
  return i18n.t(key, options) || key;
};

// Helper function to add logo to PDF
const addLogoToPDF = (doc: jsPDF, x: number, y: number, width: number, height: number): void => {
  try {
    if (COMPANY_LOGO) {
      doc.addImage(COMPANY_LOGO, 'PNG', x, y, width, height);
    }
  } catch (error) {
    console.warn('Error adding logo to PDF:', error);
  }
};

// Helper function to add watermark to PDF
const addWatermarkToPDF = (doc: jsPDF): void => {
  try {
    if (WATERMARK_IMAGE) {
      const pageCount = doc.getNumberOfPages();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        // Add watermark in the center of the page with low opacity
        doc.saveGraphicsState();
        doc.setGState(new doc.GState({ opacity: 0.1 }));
        doc.addImage(
          WATERMARK_IMAGE, 
          'PNG', 
          pageWidth / 2 - 50, 
          pageHeight / 2 - 50, 
          100, 
          100
        );
        doc.restoreGraphicsState();
      }
    }
  } catch (error) {
    console.warn('Error adding watermark to PDF:', error);
  }
};

// Helper function to format currency in PDF
const formatCurrencyForPDF = (amount: number, currency: Currency = Currency.MXN): string => {
  const convertedValue = convertCurrency(amount, Currency.MXN, currency);
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${Math.ceil(convertedValue).toLocaleString('es-MX')}`;
};

// Helper function to get cargo type translated name
const getCargoTypeName = (cargoType: string): string => {
  // Map Spanish cargo types to English translation keys
  const cargoTypeMap: Record<string, string> = {
    'mercanciageneral': 'generalmerchandise',
    'alimentosperecederos': 'perishablefood',
    'alimentosnopereceros': 'nonperishablefood',
    'materialesdecostruccion': 'constructionmaterials',
    'productosagricolas': 'agriculturalproducts',
    'mobiliario': 'furniture',
    'ropaytextiles': 'clothingtextiles',
    'productoselectronicos': 'electronics',
    'partesautomotrices': 'automotiveparts',
    'electrodomesticos': 'householdappliances',
    'aceroymetales': 'steelmetals',
    'productosquimicos': 'chemicals',
    'materialespeligrosos': 'hazardousmaterials',
    'productosfragiles': 'fragileitems',
    'mercanciadealotvalor': 'highvaluegoods',
    'maquinaria': 'machinery',
    'ganado': 'livestock',
    'productosfarmaceuticos': 'pharmaceuticals',
    'productosdemaderas': 'woodproducts',
    'otros': 'other'
  };

  // Normalize the cargo type key
  const normalizedKey = cargoType.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove accents
    .replace(/ /g, ''); // Remove spaces

  // Map to English key if it exists, otherwise use normalized key
  const mappedKey = cargoTypeMap[normalizedKey] || normalizedKey;

  const translationKey = `cargo.${mappedKey}`;
  const translation = t(translationKey);
  return translation !== translationKey ? translation : cargoType;
};

// Helper function to get vehicle type translated name
const getVehicleTypeName = (vehicleType: string): string => {
  // Normalize the key by removing spaces and accents
  const normalizedKey = vehicleType.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove all accents
    .replace(/ /g, ''); // Remove all spaces

  const translationKey = `vehicles.${normalizedKey}`;
  const translation = t(translationKey);
  return translation !== translationKey ? translation : vehicleType;
};

// Helper function to get trailer type translated name
const getTrailerTypeName = (trailerType: string | null): string => {
  if (!trailerType) return t('quotationForm.noTrailer');

  // Normalize the key by removing spaces and accents
  let normalizedKey = trailerType.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // Remove all accents
    .replace(/ /g, ''); // Remove all spaces

  // Handle special case for lowboy
  if (normalizedKey === 'lowboyocamabaja') {
    normalizedKey = 'lowboy';
  }

  const translationKey = `trailers.${normalizedKey}`;
  const translation = t(translationKey);
  return translation !== translationKey ? translation : trailerType;
};

// Helper function to add map image to PDF
const addMapImageToPDF = async (
  doc: jsPDF, 
  mapImageUrl: string | undefined, 
  x: number, 
  y: number, 
  width: number, 
  height: number
): Promise<boolean> => {
  if (!mapImageUrl) return false;
  
  try {
    let imageData: string;
    
    if (mapImageUrl.startsWith('data:')) {
      // It's already a data URL
      imageData = mapImageUrl;
    } else {
      // It's a URL, fetch it
      const fetchedData = await fetchImageAsDataUrl(mapImageUrl);
      if (!fetchedData) return false;
      imageData = fetchedData;
    }
    
    // Add the image to PDF
    doc.addImage(imageData, 'JPEG', x, y, width, height);
    return true;
  } catch (error) {
    console.error('Error adding map image to PDF:', error);
    return false;
  }
};

export const generateQuotationPDF = async (
  quotation: QuotationData,
  mapImageUrl?: string,
  currency: Currency = Currency.MXN
): Promise<jsPDF> => {
  const doc = new jsPDF();
  let yPosition = 20;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);

  // Company Header Section
  doc.setFontSize(18);
  doc.setTextColor(COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf.company.name'), margin, yPosition);
  yPosition += 8;
  
  // Company contact information
  doc.setFontSize(9);
  doc.setTextColor(COLORS.gray[600]);
  doc.setFont('helvetica', 'normal');
  doc.text(`${t('pdf.company.address')} | ${t('pdf.company.email')} | ${t('pdf.company.website')}`, margin, yPosition);
  yPosition += 10;
  
  // Add logo in top right corner
  addLogoToPDF(doc, pageWidth - 45, 10, 35, 17.5);

  // Date and validity
  doc.setFontSize(10);
  doc.setTextColor(COLORS.gray[600]);
  doc.setFont('helvetica', 'normal');
  const currentDate = new Date().toLocaleDateString();
  doc.text(`${t('pdf.date')}: ${currentDate}`, margin, yPosition);
  yPosition += 8;

  // Cost Total Section (Highlighted)
  yPosition += 10;
  doc.setFillColor(COLORS.primary);
  doc.rect(margin, yPosition - 5, contentWidth, 25, 'F');
  
  doc.setFontSize(16);
  doc.setTextColor(COLORS.white);
  doc.setFont('helvetica', 'bold');
  doc.text(t('quotationForm.result.totalCost'), margin + 5, yPosition + 5);
  
  doc.setFontSize(20);
  const totalCostText = `${formatCurrencyForPDF(quotation.totalCost, currency)} ${currency}`;
  const textWidth = doc.getTextWidth(totalCostText);
  doc.text(totalCostText, pageWidth - margin - textWidth - 5, yPosition + 15);
  yPosition += 35;

  // Trip Details Section
  doc.setFontSize(14);
  doc.setTextColor(COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(t('quotationForm.result.tripDetails'), margin, yPosition);
  yPosition += 10;

  doc.setFontSize(10);
  doc.setTextColor(COLORS.gray[700]);
  doc.setFont('helvetica', 'normal');

  const tripDetails: [string, string][] = [
    [t('pdf.origin'), quotation.originAddress || quotation.origin],
    [t('pdf.destination'), quotation.destinationAddress || quotation.destination]
  ];

  // Add additional destinations (waypoints) if they exist
  if (quotation.waypoints && quotation.waypoints.length > 0) {
    quotation.waypoints.forEach((waypoint, index) => {
      tripDetails.push([`${t('pdf.additionalDestination')} ${index + 1}`, waypoint]);
    });
  }

  // Add distance information
  tripDetails.push(
    [t('pdf.distance'), `${quotation.distance.toFixed(2)} km`],
    [t('pdf.totalDistance'), `${(quotation.distance * 2).toFixed(2)} km`]
  );

  tripDetails.forEach(([label, value]) => {
    const labelWithColonAndSpace = `${label}: `;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.gray[700]);
    doc.text(labelWithColonAndSpace, margin, yPosition);
    
    const labelAndColonWidth = doc.getTextWidth(labelWithColonAndSpace);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.gray[800]);
    
    const startXForValue = margin + labelAndColonWidth;
    const maxWidthForValue = contentWidth - labelAndColonWidth;
    
    const lines = doc.splitTextToSize(value, maxWidthForValue);
    doc.text(lines, startXForValue, yPosition);
    
    yPosition += lines.length * 5 + 8;
  });

  yPosition += 5;

  // Equipment Configuration Section
  doc.setFontSize(14);
  doc.setTextColor(COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(t('quotationForm.result.configuration'), margin, yPosition);
  yPosition += 10;

  doc.setFontSize(10);
  doc.setTextColor(COLORS.gray[700]);
  doc.setFont('helvetica', 'normal');

  const fuelUnitLabel = quotation.fuelPriceUnit === FuelUnit.GALLON ? t('common.gallon') : t('common.liter');
  const configDetails = [
    [t('pdf.unitType'), getVehicleTypeName(quotation.vehicleType)],
    ...(quotation.trailerType ? [[t('pdf.trailerType'), getTrailerTypeName(quotation.trailerType)]] : []),
    [t('pdf.cargoType'), getCargoTypeName(quotation.cargoType)],
    ...(quotation.riskFactor && quotation.riskFactor > 1.0 ? [[t('pdf.riskFactor'), `${quotation.riskFactor.toFixed(2)}x`]] : []),
   [t('quotationForm.fuelPrice'), `${getCurrencySymbol(Currency.MXN)}${quotation.fuelPrice.toFixed(2)}/${fuelUnitLabel}`],
   [t('quotationForm.result.currency'), currency]
  ];

  configDetails.forEach(([label, value]) => {
    const labelWithColonAndSpace = `${label}: `;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(COLORS.gray[700]);
    doc.text(labelWithColonAndSpace, margin, yPosition);
    
    const labelAndColonWidth = doc.getTextWidth(labelWithColonAndSpace);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(COLORS.gray[800]);
    
    const startXForValue = margin + labelAndColonWidth;
    const maxWidthForValue = contentWidth - labelAndColonWidth;
    
    const lines = doc.splitTextToSize(value, maxWidthForValue);
    doc.text(lines, startXForValue, yPosition);
    
    yPosition += lines.length * 5 + 8;
  });

  yPosition += 10;

  // Cost Breakdown Section

  if (quotation.costBreakdown) {
    const costData: string[][] = [];
    
    // Add all cost items except tollCost first
    Object.entries(quotation.costBreakdown).forEach(([key, value]) => {
      if (key !== 'tollCost') {
        const translatedKey = t(`quotationForm.costItems.${key}`);
        const formattedValue = formatCurrencyForPDF(value, currency);
        costData.push([translatedKey, formattedValue]);
      }
    });

    // Add toll details if available
    if (quotation.tollDetails && quotation.tollDetails.tollStops.length > 0) {
      // Add section header for tolls
      costData.push([t('quotationForm.costItems.tollDetailsSectionTitle'), '']);
      
      // Add individual toll stops (one-way costs)
      quotation.tollDetails.tollStops.forEach((stop) => {
        const stopCost = formatCurrencyForPDF(stop.cost, currency);
        costData.push([`  ${stop.name}`, stopCost]);
      });
      
      // Add total toll cost (includes round trip if applicable)
      if (quotation.costBreakdown.tollCost) {
        const totalTollCost = formatCurrencyForPDF(quotation.costBreakdown.tollCost, currency);
        costData.push([t('quotationForm.costItems.tollCost'), totalTollCost]);
        
        // Add note for round trip if not multi-segment
        if (!quotation.isMultiSegmentRoute) {
          costData.push(['  ' + t('tollDetails.tollNote'), '']);
        }
      }
    }

    // Add total
    costData.push(['', '']); // Empty row for spacing
    costData.push([t('quotationForm.costItems.total'), formatCurrencyForPDF(quotation.totalCost, currency)]);

    // Cost Breakdown Table
    // Estimate table height to ensure it fits on one page
    const estimatedHeaderHeight = 24; // Estimated height for header row (14pt fontSize + 2*3pt cellPadding + border)
    const estimatedBodyRowHeight = 20; // Estimated height for body rows (9pt fontSize + 2*3pt cellPadding + border)
    const estimatedTableHeight = estimatedHeaderHeight + (costData.length * estimatedBodyRowHeight);

    const availableHeightOnCurrentPage = pageHeight - yPosition - margin; // Subtract bottom margin

    // If estimated table height exceeds available space, add a new page
    if (estimatedTableHeight > availableHeightOnCurrentPage) {
      console.log(`Cost breakdown table height (${estimatedTableHeight}px) exceeds available space (${availableHeightOnCurrentPage}px), adding new page`);
      doc.addPage();
      yPosition = margin; // Reset Y position to top margin of new page
    }

    // Add the cost breakdown title here, after page break logic
    doc.setFontSize(14);
    doc.setTextColor(COLORS.primary);
    doc.setFont('helvetica', 'bold');
    doc.text(t('quotationForm.result.costBreakdown'), margin, yPosition);
    yPosition += 10;

    // AutoTable for Cost Breakdown
    autoTable(doc, {
      startY: yPosition,
      head: [[t('pdf.concept'), t('pdf.amount')]],
      body: costData,
      theme: 'grid',
      headStyles: {
        fillColor: COLORS.primary,
        textColor: COLORS.white,
        fontSize: 10,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: COLORS.gray[700]
      },
      alternateRowStyles: {
        fillColor: COLORS.gray[50]
      },
      styles: {
        cellPadding: 3,
        overflow: 'linebreak',
        cellWidth: 'wrap'
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.7 },
        1: { cellWidth: contentWidth * 0.3, halign: 'right', fontStyle: 'bold' }
      },
      didParseCell: (data) => {
        // Style the total row
        if (data.row.index === costData.length - 1) {
          data.cell.styles.fillColor = COLORS.primary;
          data.cell.styles.textColor = COLORS.white;
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 12;
        }
        // Style section headers
        if (data.cell.text[0] === t('quotationForm.costItems.tollDetailsSectionTitle')) {
          data.cell.styles.fillColor = COLORS.gray[100];
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 15;
  }


  // Security Information Section
  if (quotation.cargoType) {
    // Check if we need a new page
    if (yPosition > pageHeight - 120) {
      doc.addPage();
      yPosition = 20;
    }
    
    const riskDetails = getCargoRiskDetails(quotation.cargoType, i18n.language);
    
    if (riskDetails) {
      doc.setFontSize(14);
      doc.setTextColor(COLORS.primary);
      doc.setFont('helvetica', 'bold');
      doc.text(t('pdf.securityInfo.title'), margin, yPosition);
      yPosition += 10;
      
      // Risk description
      doc.setFontSize(12);
      doc.setTextColor(COLORS.gray[700]);
      doc.setFont('helvetica', 'bold');
      doc.text(t('pdf.securityInfo.description'), margin, yPosition);
      yPosition += 7;
      
      doc.setFontSize(10);
      doc.setTextColor(COLORS.gray[800]);
      doc.setFont('helvetica', 'normal');
      const descriptionLines = doc.splitTextToSize(riskDetails.description, contentWidth);
      doc.text(descriptionLines, margin, yPosition);
      yPosition += descriptionLines.length * 5 + 5;
      
      // Security recommendations
      doc.setFontSize(12);
      doc.setTextColor(COLORS.gray[700]);
      doc.setFont('helvetica', 'bold');
      doc.text(t('pdf.securityInfo.recommendationsTitle'), margin, yPosition);
      yPosition += 7;
      
      doc.setFontSize(10);
      doc.setTextColor(COLORS.gray[800]);
      doc.setFont('helvetica', 'normal');
      
      riskDetails.securityRecommendations.forEach((recommendation, index) => {
        const bulletPoint = `• ${recommendation}`;
        const lines = doc.splitTextToSize(bulletPoint, contentWidth - 10);
        doc.text(lines, margin + 5, yPosition);
        yPosition += lines.length * 5 + 2;
      });
      
      yPosition += 5;
      
      // Responsibility notes
      doc.setFontSize(9);
      doc.setTextColor(COLORS.gray[600]);
      doc.setFont('helvetica', 'italic');
      const noteLines1 = doc.splitTextToSize(t('pdf.securityInfo.responsibilityNote'), contentWidth);
      doc.text(noteLines1, margin, yPosition);
      yPosition += noteLines1.length * 4 + 3;
      
      const noteLines2 = doc.splitTextToSize(t('pdf.securityInfo.regulationsNote'), contentWidth);
      doc.text(noteLines2, margin, yPosition);
      yPosition += noteLines2.length * 4 + 10;
    }
  }

  // Check if we need a new page
  if (yPosition > pageHeight - 80) {
    doc.addPage();
    yPosition = 20;
  }

  // Map Section
  doc.setFontSize(14);
  doc.setTextColor(COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf.mapRoute'), margin, yPosition);
  yPosition += 10;

  // Add map image
  const mapImageAdded = mapImageUrl ? await addMapImageToPDF( // This line was causing the error
    doc, 
    mapImageUrl, 
    margin, 
    yPosition, 
    contentWidth, 
    80
  ) : false;

  if (mapImageAdded) {
    yPosition += 85;
  } else {
    // Add placeholder for map
    doc.setFillColor(COLORS.gray[100]);
    doc.rect(margin, yPosition, contentWidth, 80, 'F');
    doc.setFontSize(12);
    doc.setTextColor(COLORS.gray[500]);
    doc.text(t('pdf.mapCantLoad'), margin + contentWidth/2, yPosition + 40, { align: 'center' });
    yPosition += 85;
  }

  // Important Notes Section
  // Check if we need a new page
  if (yPosition > pageHeight - 60) {
    doc.addPage();
    yPosition = 20;
  }
  
  doc.setFontSize(14);
  doc.setTextColor(COLORS.primary);
  doc.setFont('helvetica', 'bold');
  doc.text(t('pdf.notes.title'), margin, yPosition);
  yPosition += 10;
  
  doc.setFontSize(10);
  doc.setTextColor(COLORS.gray[800]);
  doc.setFont('helvetica', 'normal');
  
  // Validity note
  const validityLines = doc.splitTextToSize(`• ${t('pdf.notes.validity')}`, contentWidth - 10);
  doc.text(validityLines, margin + 5, yPosition);
  yPosition += validityLines.length * 5 + 3;
  
  // Terms reference
  const termsLines = doc.splitTextToSize(`• ${t('pdf.notes.termsReference')}`, contentWidth - 10);
  doc.text(termsLines, margin + 5, yPosition);
  yPosition += termsLines.length * 5 + 3;
  
  // Disclaimer
  const disclaimerLines = doc.splitTextToSize(`• ${t('pdf.notes.disclaimer')}`, contentWidth - 10);
  doc.text(disclaimerLines, margin + 5, yPosition);
  yPosition += disclaimerLines.length * 5 + 10;

  // Add watermark
  addWatermarkToPDF(doc);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(COLORS.gray[500]);
  doc.setFont('helvetica', 'normal');
  const footerText = `© 2025 ${t('pdf.company.name')}. ${t('common.copyright')}`;
  const footerWidth = doc.getTextWidth(footerText);
  doc.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 10);

  // Page numbers
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(COLORS.gray[500]);
    doc.text(`${t('pdf.page')} ${i} ${t('pdf.of')} ${pageCount}`, pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  return doc;
};